import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { SoundStatus } from '../../post-interface';
import { SourceDetails } from './Posts';
import { HeaderSettingsContainer } from '../HeaderSettings';
import { VolumeCheck } from '../VolumeCheck';
import { MenuColumn, MenuLabel, MenuRow, Options } from '../Options';

export const AdvancedFeedHeader = ({
    source,
    image,
    url,
    soundStatus: soundsEnabled,
    selectedTweetTypes,
    soundTweetTypes,
    onSoundStatusChange,
    onTweetTypesUpdate,
}: {
    source: Source;
    soundStatus: SoundStatus;
    selectedTweetTypes: Set<string>;
    soundTweetTypes: Set<string>;
    onSoundStatusChange: (soundStatus: SoundStatus) => void;
    onTweetTypesUpdate: (username: string, newSelectedTweetTypes: Set<string>, newSoundTweetTypes: Set<string>) => void;
} & SourceDetails) => {
    const { username } = source;

    const channelsWithoutMenu = new Set(['gdhdhdhdhgf', 'BWEnews']);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [customSound, setCustomSound] = useState<string | null>(null);
    const [isCustomSelected, setIsCustomSelected] = useState(false);

    useEffect(() => {
        const savedSound = localStorage.getItem(`customSound_${username}`);
        const savedSelection = localStorage.getItem(`soundChoice_${username}`);
        if (savedSound) setCustomSound(savedSound);
        setIsCustomSelected(savedSelection === 'custom');
    }, [username]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                setCustomSound(result);
                localStorage.setItem(`customSound_${username}`, result);
                setIsCustomSelected(true);
                localStorage.setItem(`soundChoice_${username}`, 'custom');

                toast.success('Custom sound uploaded');
            };
            reader.readAsDataURL(file);
        } else {
            alert('Please select an audio file.');
        }
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value === 'default') {
            setIsCustomSelected(false);
            setCustomSound(null);
            localStorage.setItem(`soundChoice_${username}`, 'default');
            localStorage.removeItem(`customSound_${username}`);
        } else if (value === 'custom') {
            if (customSound) {
                setIsCustomSelected(true);
                localStorage.setItem(`soundChoice_${username}`, 'custom');
            } else {
                toast.info('First load the sound, then you can choose "Custom" sound');
            }
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const isCustomDisabled = !customSound;

    useEffect(() => {
        localStorage.setItem(`selectedTweetTypes_${username}`, JSON.stringify(Array.from(selectedTweetTypes)));
    }, [selectedTweetTypes, username]);

    useEffect(() => {
        localStorage.setItem(`soundTweetTypes_${username}`, JSON.stringify(Array.from(soundTweetTypes)));
    }, [soundTweetTypes, username]);

    return (
        <>
            <div className='icon-naming-link'>
                <img className='icon' src={`data:image/jpeg;base64,${image}`} alt='Source Icon' />
                <div className='name-url'>
                    <h3>{source.username}</h3>
                    <a
                        className='link-to-channel'
                        href={url ? url : '#'}
                        target='_blank'
                        rel='noreferrer'
                        onClick={(e) => {
                            if (!url) {
                                e.preventDefault();
                                console.log('URL not provided');
                            }
                        }}
                    >
                        <h3>{url && url.includes('https://') ? url.slice(8) : url}</h3>
                    </a>
                </div>
            </div>
            <HeaderSettingsContainer>
                <VolumeCheck
                    enabled={soundsEnabled[source.username]}
                    onChecked={() => {
                        const newSoundsEnabled = { ...soundsEnabled };
                        newSoundsEnabled[source.username] = !newSoundsEnabled[source.username];
                        onSoundStatusChange(newSoundsEnabled);
                    }}
                />
                <Options>
                    <MenuRow>
                        {!channelsWithoutMenu.has(source.username) && (
                            <MenuColumn title='DISPLAY'>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={selectedTweetTypes.has('tweet')}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('tweet')) {
                                                newSelectedTweetTypes.delete('tweet');
                                            } else {
                                                newSelectedTweetTypes.add('tweet');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Tweets
                                </MenuLabel>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={selectedTweetTypes.has('retweet')}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('retweet')) {
                                                newSelectedTweetTypes.delete('retweet');
                                            } else {
                                                newSelectedTweetTypes.add('retweet');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Retweets
                                </MenuLabel>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={selectedTweetTypes.has('reply')}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('reply')) {
                                                newSelectedTweetTypes.delete('reply');
                                            } else {
                                                newSelectedTweetTypes.add('reply');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Replies
                                </MenuLabel>
                            </MenuColumn>
                        )}
                        {!channelsWithoutMenu.has(source.username) && (
                            <MenuColumn title='SOUND'>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={soundTweetTypes.has('tweet')}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('tweet')) {
                                                newSoundTweetTypes.delete('tweet');
                                            } else {
                                                newSoundTweetTypes.add('tweet');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Tweets
                                </MenuLabel>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={soundTweetTypes.has('retweet')}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('retweet')) {
                                                newSoundTweetTypes.delete('retweet');
                                            } else {
                                                newSoundTweetTypes.add('retweet');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Retweets
                                </MenuLabel>
                                <MenuLabel>
                                    <input
                                        type='checkbox'
                                        checked={soundTweetTypes.has('reply')}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('reply')) {
                                                newSoundTweetTypes.delete('reply');
                                            } else {
                                                newSoundTweetTypes.add('reply');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Replies
                                </MenuLabel>
                            </MenuColumn>
                        )}
                    </MenuRow>
                    <MenuRow>
                        <MenuColumn centerTitle title='CHOOSE SOUND'>
                            <div className='radio-buttons'>
                                <label className='radio-label'>
                                    <input type='radio' name={`sound_${username}`} value='default' checked={!isCustomSelected} onChange={handleRadioChange} />
                                    <span className='custom-radio'></span> Default
                                </label>
                                <label
                                    className='radio-label'
                                    onClick={(e) => {
                                        if (isCustomDisabled || !customSound) {
                                            e.preventDefault();
                                            toast.info('First load the sound, then you can choose "Custom" sound');
                                        }
                                    }}
                                >
                                    <input
                                        type='radio'
                                        name={`sound_${username}`}
                                        value='custom'
                                        checked={isCustomSelected}
                                        onChange={handleRadioChange}
                                        disabled={isCustomDisabled || !customSound}
                                    />
                                    <span className='custom-radio'></span> Custom
                                </label>
                                <div className='file-upload'>
                                    <input ref={fileInputRef} type='file' style={{ display: 'none' }} accept='audio/*' onChange={handleFileChange} />
                                    <button onClick={handleButtonClick} className='custom-file-upload'>
                                        <span className='icon' />
                                    </button>
                                </div>
                            </div>
                        </MenuColumn>
                    </MenuRow>
                </Options>
            </HeaderSettingsContainer>
        </>
    );
};
